import React, { useEffect, useState, useRef } from "react";
import { Col, notification, Row, Space, Spin } from "antd";
import { validateSurveyDocFile } from "../utils/fileValidation";
import {
  TextInputContainer,
  TextInputArea,
  TextArea,
  BottomControls,
  AttachmentSection,
  AttachmentIcon,
  RightControls,
  SendButton,
  FilePreview,
  FileInfo,
  FileName,
  FileSize,
  RemoveFileButton,
  UploadProgress,
  ProgressBar,
  ProgressFill,
  ProgressText,
  HiddenFileInput,
} from "./ChatBoxStyle";
import { ThemeFilterBtn } from "../pages/Styles/Theme";
import Lottie from "lottie-react";
import loaderJson from "../jsons/upload.json";
const ChatBox = ({
  value,
  placeholder,
  minHeight,
  onChange,
  onSend,
  onAttachmentClick,
  isSending,
  disabled,
  attachmentLabel = "Upload Discussion Guide",
  enableFileUpload = false,
  uploadedFile = null,
  onFileSelect,
  onFileRemove,
  uploadProgress = 0,
  isUploading = false,
  acceptedFileTypes = ".pdf,.txt,.doc,.docx,.csv,.xls,.xlsx",
  maxFileSize = 10 * 1024 * 1024,
  className = "",
  buildPrompt,
  onPromptBuilderClick,
  libraryPrompt,
  onPromptLibraryClick,
}) => {
  const [internalValue, setInternalValue] = useState(value || "");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (typeof value === "string") {
      setInternalValue(value);
    }
  }, [value]);

  const handleChange = (event) => {
    const nextValue = event.target.value;
    setInternalValue(nextValue);
    onChange?.(event);
  };

  const handleSend = () => {
    const payload = typeof value === "string" ? value : internalValue;
    onSend?.(payload);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachmentClick = () => {
    if (enableFileUpload) {
      fileInputRef.current?.click();
    } else if (onAttachmentClick) {
      onAttachmentClick();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Use centralized validation - blocks invalid files
    const validation = validateSurveyDocFile(file);
    if (!validation.valid) {
      notification.error({
        message: 'Invalid File',
        description: validation.error,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    onFileSelect?.(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '/images/pdf-icon.svg';
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    // Map file extensions to their corresponding icons
    if (extension === 'pdf') {
      return '/images/pdf-icon.svg';
    }
    if (extension === 'txt') {
      return '/images/txt-icon.svg';
    }
    if (extension === 'doc' || extension === 'docx') {
      return '/images/doc-icon.svg';
    }
    if (extension === 'csv') {
      return '/images/csv-icon.svg';
    }
    if (extension === 'xls' || extension === 'xlsx') {
      return '/images/excel-icon.svg';
    }
    
    // Default fallback
    return '/images/pdf-icon.svg';
  };

  return (
    <TextInputContainer>
      <TextInputArea className={className}>
        <TextArea
          placeholder={placeholder}
          value={typeof value === "string" ? value : internalValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          $minHeight={minHeight}
        />

        {uploadedFile && (
          <FilePreview>
            <FileInfo>
              <Space>
                {isUploading ? (
                  <UploadProgress>
                    <Lottie animationData={loaderJson} loop={true} />
                  </UploadProgress>
                ) : (
                  <img 
                    width={40} 
                    height={40} 
                    src={getFileIcon(uploadedFile.name)} 
                    alt="file" 
                  />
                )}
                <div>
                  <FileName>{uploadedFile.name}</FileName>
                  <FileSize>{formatFileSize(uploadedFile.size)}</FileSize>
                </div>
              </Space>
            </FileInfo>
            {!isUploading && (
              <RemoveFileButton onClick={handleRemoveFile}>
                <ThemeFilterBtn shape="circle" icon={<img src="/images/delete-icon.svg" alt="file" />} />
              </RemoveFileButton>
            )}
          </FilePreview>
        )}
        <BottomControls>
          {buildPrompt ? (
            <Space>
              {libraryPrompt ? (null) 
               :  
              <AttachmentSection type="button" onClick={onPromptBuilderClick}>
                <AttachmentIcon>
                  <img src="/images/shine-icon.svg" alt="attachment" />
                </AttachmentIcon>
                Build a Prompt
              </AttachmentSection> }
                <AttachmentSection type="button" onClick={onPromptLibraryClick}>
                  <AttachmentIcon>
                    <img src="/images/prompt-lib-icon.svg" alt="attachment" />
                  </AttachmentIcon>
                  Prompt Library
                </AttachmentSection>
            </Space>
          ):
          <AttachmentSection type="button" onClick={handleAttachmentClick}>
            <AttachmentIcon>
              <img src="/images/attach-icon.svg" alt="attachment" />
            </AttachmentIcon>
            {attachmentLabel}
          </AttachmentSection>}
          
          <RightControls>
            <SendButton type="button" onClick={handleSend} disabled={isSending || disabled || isUploading}>
              <img src="/images/send-icon.svg" alt="Send" />
            </SendButton>
          </RightControls>
        </BottomControls>
      </TextInputArea>

      {enableFileUpload && (
        <HiddenFileInput type="file" ref={fileInputRef} onChange={handleFileChange} accept={acceptedFileTypes} />
      )}
    </TextInputContainer>
  );
};

export default ChatBox;
